import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';

const ContentRenderer = ({ content }) => {
    // Function to decode HTML entities (like &lt;a&gt; to <a>)
    const decodeHtmlEntities = (htmlString) => {
        if (!htmlString || typeof htmlString !== 'string') {
            return '';
        }

        // Decode common HTML entities that might be present in CKEditor content
        return htmlString
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&amp;/g, '&')
            .replace(/&quot;/g, '"')
            .replace(/&#39;/g, "'")
            .replace(/&nbsp;/g, ' ');
    };

    const processContent = (htmlString) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, 'text/html');

        // Handle images and their containers (including CKEditor 5 figure elements)
        const imageContainers = doc.querySelectorAll('img, figure');

        imageContainers.forEach((element, index) => {
            let img;
            let container = null;

            if (element.tagName === 'IMG') {
                img = element;
                container = img.closest('figure') || img.parentElement;
            } else if (element.tagName === 'FIGURE') {
                img = element.querySelector('img');
                container = element;
                if (!img) return; // Skip figures without images
            }

            if (!img) return;

            // Set basic image attributes
            img.setAttribute('crossorigin', 'anonymous');
            img.setAttribute('width', 'auto');
            img.setAttribute('height', '100%');

            // Get all possible alignment indicators
            const imgStyle = img.getAttribute('style') || '';
            const imgClass = img.getAttribute('class') || '';
            const containerStyle = container ? (container.getAttribute('style') || '') : '';
            const containerClass = container ? (container.getAttribute('class') || '') : '';
            const figureClass = container && container.tagName === 'FIGURE' ? containerClass : '';

            console.log(`Processing image ${index + 1}:`, {
                src: img.src,
                imgStyle,
                imgClass,
                containerStyle,
                containerClass,
                figureClass,
                containerTag: container ? container.tagName : 'none',
                fullHTML: container ? container.outerHTML : img.outerHTML
            });

            // Comprehensive alignment detection for CKEditor 5
            let detectedAlignment = 'center'; // default

            // Check for LEFT alignment (multiple possible formats)
            if (
                // Inline styles
                imgStyle.includes('float:left') || imgStyle.includes('float: left') ||
                containerStyle.includes('float:left') || containerStyle.includes('float: left') ||
                imgStyle.includes('text-align:left') || imgStyle.includes('text-align: left') ||
                containerStyle.includes('text-align:left') || containerStyle.includes('text-align: left') ||

                // CSS Classes (CKEditor 5 formats)
                imgClass.includes('image-style-align-left') ||
                imgClass.includes('image-align-left') ||
                imgClass.includes('align-left') ||
                imgClass.includes('ck-align-left') ||
                containerClass.includes('image-style-align-left') ||
                containerClass.includes('image-align-left') ||
                containerClass.includes('align-left') ||
                containerClass.includes('ck-align-left') ||
                figureClass.includes('image-style-align-left') ||
                figureClass.includes('image') && figureClass.includes('align-left')
            ) {
                detectedAlignment = 'left';
            }
            // Check for RIGHT alignment
            else if (
                // Inline styles
                imgStyle.includes('float:right') || imgStyle.includes('float: right') ||
                containerStyle.includes('float:right') || containerStyle.includes('float: right') ||
                imgStyle.includes('text-align:right') || imgStyle.includes('text-align: right') ||
                containerStyle.includes('text-align:right') || containerStyle.includes('text-align: right') ||

                // CSS Classes (CKEditor 5 formats)
                imgClass.includes('image-style-align-right') ||
                imgClass.includes('image-align-right') ||
                imgClass.includes('align-right') ||
                imgClass.includes('ck-align-right') ||
                containerClass.includes('image-style-align-right') ||
                containerClass.includes('image-align-right') ||
                containerClass.includes('align-right') ||
                containerClass.includes('ck-align-right') ||
                figureClass.includes('image-style-align-right') ||
                figureClass.includes('image') && figureClass.includes('align-right')
            ) {
                detectedAlignment = 'right';
            }
            // Check for CENTER alignment
            else if (
                // Inline styles
                imgStyle.includes('text-align:center') || imgStyle.includes('text-align: center') ||
                containerStyle.includes('text-align:center') || containerStyle.includes('text-align: center') ||
                imgStyle.includes('margin:auto') || imgStyle.includes('margin: auto') ||
                imgStyle.includes('margin:0 auto') || imgStyle.includes('margin: 0 auto') ||
                containerStyle.includes('margin:auto') || containerStyle.includes('margin: auto') ||
                containerStyle.includes('margin:0 auto') || containerStyle.includes('margin: 0 auto') ||

                // CSS Classes (CKEditor 5 formats)
                imgClass.includes('image-style-align-center') ||
                imgClass.includes('image-align-center') ||
                imgClass.includes('align-center') ||
                imgClass.includes('ck-align-center') ||
                containerClass.includes('image-style-align-center') ||
                containerClass.includes('image-align-center') ||
                containerClass.includes('align-center') ||
                containerClass.includes('ck-align-center') ||
                figureClass.includes('image-style-align-center') ||
                figureClass.includes('image') && figureClass.includes('align-center')
            ) {
                detectedAlignment = 'center';
            }

            console.log(`Image ${index + 1} detected as ${detectedAlignment.toUpperCase()} aligned`);

            // Apply alignment styles
            img.setAttribute('data-align', detectedAlignment);

            if (detectedAlignment === 'left') {
                img.style.setProperty('float', 'left', 'important');
                img.style.setProperty('margin', '10px 15px 10px 0', 'important');
                img.style.setProperty('clear', 'none', 'important');
                img.style.setProperty('display', 'inline', 'important');
                if (container && container !== img.parentElement) {
                    container.style.setProperty('float', 'left', 'important');
                    container.style.setProperty('margin', '10px 15px 10px 0', 'important');
                }
            } else if (detectedAlignment === 'right') {
                img.style.setProperty('float', 'right', 'important');
                img.style.setProperty('margin', '10px 0 10px 15px', 'important');
                img.style.setProperty('clear', 'none', 'important');
                img.style.setProperty('display', 'inline', 'important');
                if (container && container !== img.parentElement) {
                    container.style.setProperty('float', 'right', 'important');
                    container.style.setProperty('margin', '10px 0 10px 15px', 'important');
                }
            } else { // center
                img.style.setProperty('display', 'block', 'important');
                img.style.setProperty('margin', '10px auto', 'important');
                img.style.setProperty('float', 'none', 'important');
                img.style.setProperty('clear', 'both', 'important');
                if (container && container !== img.parentElement) {
                    container.style.setProperty('display', 'block', 'important');
                    container.style.setProperty('margin', '10px auto', 'important');
                    container.style.setProperty('float', 'none', 'important');
                }
            }

            console.log(`Image ${index + 1} after processing:`, img.outerHTML);
        });

        // Handle anchors with debugging
        const anchors = doc.querySelectorAll('a');
        console.log(`Found ${anchors.length} anchors to process`);

        anchors.forEach((anchor, index) => {
            console.log(`Processing anchor ${index + 1}:`, anchor.outerHTML);
            console.log(`Anchor text content:`, anchor.textContent);
            console.log(`Anchor href:`, anchor.getAttribute('href'));
            console.log(`Anchor title:`, anchor.getAttribute('title'));

            if (anchor.hasAttribute('href')) {
                const href = anchor.getAttribute('href');
                const textContent = anchor.textContent?.trim();

                // Check if this looks like a reference link (number in brackets or just a number)
                const isReferenceLink = (href && /^\d+$/.test(href)) || 
                                      (textContent && /^\[\d+\]$/.test(textContent)) ||
                                      (textContent && /^\d+$/.test(textContent)) ||
                                      (textContent && /^[²³¹⁴⁵]$/.test(textContent)) ||
                                      (textContent && /^\[[²³¹⁴⁵]\]$/.test(textContent)) ||
                                      (href && href.startsWith('#'));

                console.log(`Is reference link:`, isReferenceLink);

                if (isReferenceLink) {
                    let referenceNumber = '';
                    
                    if (href && /^\d+$/.test(href)) {
                        // Numeric href like <a href="10" title="10">10</a>
                        referenceNumber = href;
                    } else if (href && href.startsWith('#')) {
                        // Internal citation like <a href="#1">
                        referenceNumber = href.replace('#', '');
                    } else if (textContent && /^\[\d+\]$/.test(textContent)) {
                        // Text content like [1], [2], etc.
                        referenceNumber = textContent.replace(/[[\]]/g, '');
                    } else if (textContent && /^\[[²³¹⁴⁵]\]$/.test(textContent)) {
                        // Text content like [²], [³], etc.
                        referenceNumber = textContent.replace(/[[\]]/g, '');
                    } else if (textContent && /^\d+$/.test(textContent)) {
                        // Just a number
                        referenceNumber = textContent;
                    } else if (textContent && /^[²³¹⁴⁵]$/.test(textContent)) {
                        // Just a superscript number
                        referenceNumber = textContent;
                    }

                    console.log(`Processing as reference link with number:`, referenceNumber);
                    
                    // Remove href to prevent default link behavior
                    anchor.removeAttribute('href');
                    
                    // Add reference link attributes and classes
                    anchor.setAttribute('data-reference-number', referenceNumber);
                    anchor.classList.add('content-reference-link');
                    
                    // Style like citation number (normal baseline, inherited size)
                    anchor.style.setProperty('font-size', 'inherit');
                    anchor.style.setProperty('vertical-align', 'baseline');
                    anchor.style.setProperty('color', '#0066cc', 'important');
                    anchor.style.setProperty('text-decoration', 'underline', 'important');
                    anchor.style.setProperty('cursor', 'pointer', 'important');
                    anchor.style.setProperty('pointer-events', 'auto', 'important');
                    anchor.style.setProperty('user-select', 'none', 'important');
                    anchor.style.setProperty('display', 'inline', 'important');
                    anchor.style.setProperty('position', 'relative', 'important');
                    anchor.style.setProperty('z-index', '10', 'important');

                    // Clean up text content
                    anchor.textContent = referenceNumber;
                    
                    console.log(`Anchor ${index + 1} after processing as reference link:`, anchor.outerHTML);
                } else {
                    // 🌍 External/real links
                    console.log(`Anchor ${index + 1} has href:`, href);

                    anchor.setAttribute('target', '_blank');
                    anchor.setAttribute('rel', 'noopener noreferrer');

                    anchor.classList.add('content-link', 'force-blue-link', 'ck-link-blue');
                    anchor.setAttribute('data-force-blue-color', 'true');
                    anchor.setAttribute('data-content-link', 'true');

                    // Clean, consistent link appearance (archive style): blue + underline only
                    anchor.style.setProperty('color', '#0066cc', 'important');
                    anchor.style.setProperty('text-decoration', 'underline', 'important');
                    anchor.style.removeProperty('border');
                    anchor.style.removeProperty('background-color');
                    anchor.style.removeProperty('padding');
                    anchor.style.removeProperty('border-radius');
                    anchor.style.removeProperty('font-weight');

                    console.log(`Anchor ${index + 1} after processing:`, anchor.outerHTML);
                }
            } else if (anchor.hasAttribute('name') || anchor.hasAttribute('id')) {
                // 📌 Internal bookmark target
                console.log(`Anchor ${index + 1} is a bookmark/internal anchor`);
                anchor.classList.add('content-anchor');
            }
        });

        // Add invisible anchors for each numbered reference under a REFERENCES heading
        try {
            const potentialContainers = Array.from(
                doc.querySelectorAll('h1,h2,h3,h4,h5,h6,strong,b,p,li,div')
            );
            let seenReferencesHeading = false;
            potentialContainers.forEach((el) => {
                const text = (el.textContent || '').trim();
                if (!seenReferencesHeading && /^references?:?$/i.test(text)) {
                    seenReferencesHeading = true;
                    return;
                }
                if (seenReferencesHeading) {
                    // Try to detect lines like "1. ..." or "1) ..."
                    const match = text.match(/^\s*(\d+)[.).]\s/);
                    if (match) {
                        const refNum = match[1];
                        if (!doc.getElementById(refNum) && !doc.getElementById(`ref-${refNum}`)) {
                            const a = doc.createElement('a');
                            a.setAttribute('id', refNum);
                            a.setAttribute('name', refNum);
                            // Add prefixed id as well to avoid collisions
                            a.setAttribute('data-alt-id', `ref-${refNum}`);
                            // Invisible marker that does not affect styling/layout
                            a.style.cssText = 'display:inline; width:0; height:0; line-height:0;';
                            el.insertBefore(a, el.firstChild);
                        }
                    }
                }
            });
        } catch (e) {
            console.warn('Reference anchoring skipped due to error', e);
        }


        // Style lists
        doc.querySelectorAll('ul, ol').forEach((list) => {
            list.style.fontSize = 'inherit';
            list.style.lineHeight = '1.6';
            list.style.margin = '10px 0';
            list.style.paddingLeft = '20px';
        });

        doc.querySelectorAll('li').forEach((item) => {
            item.style.fontSize = 'inherit';
            item.style.lineHeight = '1.6';
            item.style.margin = '5px 0';
        });

        doc.querySelectorAll('head, script, style').forEach((tag) => tag.remove());

        return doc.body.innerHTML;
    };

    // First decode HTML entities, then process and sanitize the content
    const decodedContent = decodeHtmlEntities(content);
    const processedContent = processContent(decodedContent);

    // Sanitize the content for security and clean rendering
    const sanitizedContent = DOMPurify.sanitize(processedContent, {
        ADD_TAGS: ['a', 'img', 'ul', 'ol', 'li'],
        ADD_ATTR: [
            'style',
            'class',
            'id',
            'name',   // keep this for <a name="1">
            'href',
            'target',
            'rel',
            'data-force-blue-color',
            'data-content-link',
            'data-ck-link-id',
            'data-link-color',
            'data-force-blue',
            'data-external-link',
            'data-reference-number',
            'crossorigin',
            'width',
            'height'
        ],
    });

    // Handle reference link clicks
    useEffect(() => {
        const handleReferenceClick = (event) => {
            const target = event.target;
            console.log('Click detected on:', target, 'Classes:', target.className, 'Data attributes:', target.dataset);

            // Delegate: find nearest element that carries the reference number
            const refEl = target.closest ? target.closest('[data-reference-number]') : null;

            if (refEl) {
                event.preventDefault();
                event.stopPropagation();

                const referenceNumber = refEl.getAttribute('data-reference-number');
                console.log('Reference link clicked:', referenceNumber);
                
                if (referenceNumber) {
                    // Convert superscript numbers to regular numbers for searching
                    let searchNum = referenceNumber;
                    if (referenceNumber === '²') searchNum = '2';
                    if (referenceNumber === '³') searchNum = '3';
                    if (referenceNumber === '¹') searchNum = '1';
                    if (referenceNumber === '⁴') searchNum = '4';
                    if (referenceNumber === '⁵') searchNum = '5';
                    
                    console.log('Searching for reference number:', searchNum);
                    
                    // First, try to find the specific reference in the document
                    // Look for elements with id or name matching the reference number
                    let referenceElement = document.getElementById(searchNum) ||
                                         document.querySelector(`[name="${searchNum}"]`) ||
                                         document.querySelector(`a[name="${searchNum}"]`) ||
                                         document.querySelector(`[data-alt-id="ref-${searchNum}"]`);
                    
                    console.log('Found reference element by id/name:', referenceElement);
                    
                    // If not found, look for an explicit anchor id; otherwise find the first block that starts with `${n}. `
                    if (!referenceElement) {
                        // Prefer element with id first (inserted in processContent)
                        referenceElement = document.getElementById(searchNum) || document.querySelector(`[data-alt-id="ref-${searchNum}"]`);
                    }
                    if (!referenceElement) {
                        const blockCandidates = document.querySelectorAll('li, p, div');
                        for (let element of blockCandidates) {
                            const text = element.textContent?.trim();
                            if (text && text.startsWith(`${searchNum}. `)) {
                                referenceElement = element;
                                console.log('Found reference element by block start:', element);
                                break;
                            }
                        }
                    }
                    if (!referenceElement) {
                        referenceElement = document.getElementById(searchNum);
                    }
                    if (!referenceElement) {
                        const blockCandidates = document.querySelectorAll('li, p, div');
                        for (let element of blockCandidates) {
                            const text = element.textContent?.trim();
                            if (text && text.startsWith(`${searchNum}. `)) {
                                referenceElement = element;
                                console.log('Found reference element by block start:', element);
                                break;
                            }
                        }
                    }
                    
                    if (referenceElement) {
                        // Scroll to the specific reference element
                        const yOffset = -80; // Adjust based on header height
                        const y = referenceElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
                        window.scrollTo({ top: y, behavior: 'smooth' });
                        console.log('Scrolled to reference element:', referenceElement);
                        return;
                    }
                    
                    // If no specific reference found, scroll to the references section
                    console.log('Reference element not found, scrolling to references section');
                    const referencesSection = document.querySelector('#info') || 
                                            document.querySelector('.references') ||
                                            document.querySelector('[class*="reference"]') ||
                                            document.querySelector('[id*="reference"]');
                    
                    console.log('Found references section:', referencesSection);
                    
                    if (referencesSection) {
                        const yOffset = -80;
                        const y = referencesSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                        window.scrollTo({ top: y, behavior: 'smooth' });
                        console.log('Scrolled to references section:', referencesSection);
                    } else {
                        // Fallback: scroll to bottom of page where references typically are
                        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                        console.log('Scrolled to bottom of page as fallback');
                    }
                }
            }
        };

        // Add event listener with multiple attempts to ensure it works
        const addEventListeners = () => {
            const contentRenderer = document.querySelector('.content-renderer');
            if (contentRenderer) {
                // Remove any existing listeners first
                contentRenderer.removeEventListener('click', handleReferenceClick, true);
                
                // Add the event listener
                contentRenderer.addEventListener('click', handleReferenceClick, true);
                console.log('Event listener added to content renderer');
                
                // Also add individual event listeners to reference links for better reliability
                const referenceLinks = contentRenderer.querySelectorAll('.content-reference-link, [data-reference-number]');
                console.log('Found reference links:', referenceLinks.length);
                
                referenceLinks.forEach((link, index) => {
                    // Remove existing listener first
                    link.removeEventListener('click', handleReferenceClick, true);
                    // Add new listener
                    link.addEventListener('click', handleReferenceClick, true);
                    
                    // Also add direct onclick handler as backup
                    link.onclick = (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const refNum = link.getAttribute('data-reference-number');
                        console.log('Direct onclick handler triggered for reference:', refNum);
                        console.log('Link element:', link);
                        console.log('Link text content:', link.textContent);
                        
                        if (refNum) {
                            // Convert superscript numbers to regular numbers for searching
                            let searchNum = refNum;
                            if (refNum === '²') searchNum = '2';
                            if (refNum === '³') searchNum = '3';
                            if (refNum === '¹') searchNum = '1';
                            if (refNum === '⁴') searchNum = '4';
                            if (refNum === '⁵') searchNum = '5';
                            
                            console.log('Searching for reference number:', searchNum);
                            
                            // Find and scroll to reference
                            let referenceElement = document.getElementById(searchNum) ||
                                                 document.querySelector(`[name="${searchNum}"]`) ||
                                                 document.querySelector(`a[name="${searchNum}"]`) ||
                                                 document.querySelector(`[data-alt-id="ref-${searchNum}"]`);
                            
                            console.log('Found reference element by id/name:', referenceElement);
                            
                            if (!referenceElement) {
                                // Look for text content that starts with the reference number followed by a period
                                const allElements = document.querySelectorAll('*');
                                for (let element of allElements) {
                                    const text = element.textContent?.trim();
                                    if (text && (text.match(new RegExp(`^${searchNum}\\.`)) || text.match(new RegExp(`^${searchNum}\\.\\s`)))) {
                                        referenceElement = element;
                                        console.log('Found reference element by text content:', element, 'Text:', text);
                                        break;
                                    }
                                }
                            }
                            
                            if (referenceElement) {
                                const yOffset = -80;
                                const y = referenceElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                window.scrollTo({ top: y, behavior: 'smooth' });
                                console.log('Scrolled to reference element:', referenceElement);
                            } else {
                                console.log('Reference element not found, scrolling to references section');
                                const referencesSection = document.querySelector('#info') || 
                                                        document.querySelector('.references') ||
                                                        document.querySelector('[class*="reference"]') ||
                                                        document.querySelector('[id*="reference"]');
                                
                                console.log('Found references section:', referencesSection);
                                
                                if (referencesSection) {
                                    const yOffset = -80;
                                    const y = referencesSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                                    window.scrollTo({ top: y, behavior: 'smooth' });
                                    console.log('Scrolled to references section:', referencesSection);
                                } else {
                                    // Fallback: scroll to bottom of page
                                    window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                                    console.log('Scrolled to bottom of page as fallback');
                                }
                            }
                        }
                    };
                    
                    console.log(`Individual event listener and onclick handler added to reference link ${index + 1}:`, link);
                });
                
                return () => {
                    contentRenderer.removeEventListener('click', handleReferenceClick, true);
                    referenceLinks.forEach(link => {
                        link.removeEventListener('click', handleReferenceClick, true);
                        link.onclick = null; // Remove onclick handler
                    });
                };
            }
            return null;
        };

        // Use multiple timeouts to ensure DOM is ready
        const timeoutId1 = setTimeout(addEventListeners, 100);
        const timeoutId2 = setTimeout(addEventListeners, 500);
        const timeoutId3 = setTimeout(addEventListeners, 1000);
        
        // Add a global click handler as a fallback to catch any superscript numbers
        const globalClickHandler = (event) => {
            const target = event.target;
            const delegated = target.closest ? target.closest('[data-reference-number]') : null;
            const textContent = (delegated ? delegated.textContent : target.textContent)?.trim();
            
            // Check if clicked element contains superscript numbers
            if (delegated || (textContent && /^[²³¹⁴⁵]$/.test(textContent))) {
                console.log('Global click handler - Superscript number clicked:', textContent);
                event.preventDefault();
                event.stopPropagation();
                
                // Convert superscript to regular number
                let searchNum = delegated ? delegated.getAttribute('data-reference-number') : textContent;
                if (textContent === '²') searchNum = '2';
                if (textContent === '³') searchNum = '3';
                if (textContent === '¹') searchNum = '1';
                if (textContent === '⁴') searchNum = '4';
                if (textContent === '⁵') searchNum = '5';
                
                console.log('Global click handler - Searching for reference number:', searchNum);
                
                // Find and scroll to reference
                let referenceElement = document.getElementById(searchNum) ||
                                     document.querySelector(`[name="${searchNum}"]`) ||
                                     document.querySelector(`a[name="${searchNum}"]`) ||
                                     document.querySelector(`[data-alt-id="ref-${searchNum}"]`);
                
                if (!referenceElement) {
                    referenceElement = document.getElementById(searchNum);
                }
                if (!referenceElement) {
                    const blockCandidates = document.querySelectorAll('li, p, div');
                    for (let element of blockCandidates) {
                        const text = element.textContent?.trim();
                        if (text && text.startsWith(`${searchNum}. `)) {
                            referenceElement = element;
                            console.log('Global click handler - Found reference element by block start:', element);
                            break;
                        }
                    }
                }
                
                if (referenceElement) {
                    const yOffset = -80;
                    const y = referenceElement.getBoundingClientRect().top + window.pageYOffset + yOffset;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                    console.log('Global click handler - Scrolled to reference element:', referenceElement);
                } else {
                    console.log('Global click handler - Reference not found, scrolling to references section');
                    const referencesSection = document.querySelector('#info') || 
                                            document.querySelector('.references') ||
                                            document.querySelector('[class*="reference"]') ||
                                            document.querySelector('[id*="reference"]');
                    
                    if (referencesSection) {
                        const yOffset = -80;
                        const y = referencesSection.getBoundingClientRect().top + window.pageYOffset + yOffset;
                        window.scrollTo({ top: y, behavior: 'smooth' });
                        console.log('Global click handler - Scrolled to references section:', referencesSection);
                    } else {
                        window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
                        console.log('Global click handler - Scrolled to bottom of page');
                    }
                }
            }
        };
        
        // Add global click handler
        document.addEventListener('click', globalClickHandler, true);
        
        return () => {
            clearTimeout(timeoutId1);
            clearTimeout(timeoutId2);
            clearTimeout(timeoutId3);
            document.removeEventListener('click', globalClickHandler, true);
        };
    }, [content]); // Re-run when content changes

    return (
        <>
            <style>
                {`
                    .content-renderer .content-reference-link {
                        cursor: pointer !important;
                        pointer-events: auto !important;
                        color: #0066cc !important;
                        text-decoration: underline !important;
                        font-size: inherit !important;
                        vertical-align: baseline !important;
                        display: inline !important;
                        position: relative !important;
                        z-index: 10 !important;
                        user-select: none !important;
                    }
                    .content-renderer .content-reference-link:hover {
                        color: #0052a3 !important;
                        text-decoration: underline !important;
                    }
                    .content-renderer [data-reference-number] {
                        cursor: pointer !important;
                        pointer-events: auto !important;
                        color: #0066cc !important;
                        text-decoration: underline !important;
                        font-size: inherit !important;
                        vertical-align: baseline !important;
                        display: inline !important;
                        position: relative !important;
                        z-index: 10 !important;
                        user-select: none !important;
                    }
                    .content-renderer [data-reference-number]:hover {
                        color: #0052a3 !important;
                        text-decoration: underline !important;
                    }
                `}
            </style>
        <div
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            className="content-renderer"
            style={{
                fontSize: 'inherit',
                lineHeight: '1.6'
            }}
        />
        </>
    );
};

// PropTypes validation
ContentRenderer.propTypes = {
    content: PropTypes.string, // Validate that content is a string
};

// Default props
ContentRenderer.defaultProps = {
    content: '',
};

export default ContentRenderer;
